import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from "@/hooks/useApiMutation";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { queryClient } from '@/lib/queryClient';
import { landingPageApi, LandingPageSection } from '@/lib/landing-page-api';
import { Edit, Save, X, Eye, EyeOff } from 'lucide-react';

export default function AdminLandingPage() {
  
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editContent, setEditContent] = useState<any>({});

  const { data: sections } = useQuery<LandingPageSection[]>({
    queryKey: ['landing-page-admin'],
    queryFn: landingPageApi.getAllSections as unknown as () => Promise<LandingPageSection[]>,
  });

  const updateMutation = useApiMutation({
    endpoint: (vars: { section: string; content?: any }) => `/api/landing-page/sections/${vars.section}`,
    method: "PUT",
    invalidate: [['landing-page-admin'], ['landing-page']],
    successMessage: "Section updated successfully",
    onSuccess: () => {
      setEditingSection(null);
    },
  });

  const toggleMutation = useApiMutation({
    endpoint: (vars: { section: string }) => `/api/landing-page/sections/${vars.section}/toggle`,
    method: "PATCH",
    invalidate: [['landing-page-admin']],
    successMessage: "Visibility updated",
  });

  const startEdit = (section: LandingPageSection) => {
    setEditingSection(section.section);
    setEditContent(section.content);
  };

  const saveEdit = () => {
    if (editingSection) {
      updateMutation.mutate({ section: editingSection, content: editContent });
    }
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setEditContent({});
  };

  const renderHeroEditor = (section: LandingPageSection) => {
    const isEditing = editingSection === 'hero';
    const content = isEditing ? editContent : section.content;

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Hero Section</CardTitle>
            <div className="flex items-center gap-2">
              <Switch checked={section.isActive} onCheckedChange={() => toggleMutation.mutate({ section: section.section })} />
              {section.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              {!isEditing && <Button size="sm" onClick={() => startEdit(section)}><Edit className="h-4 w-4" /></Button>}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Title</label>
                <Textarea value={content.title} onChange={(e) => setEditContent({ ...editContent, title: e.target.value })} rows={2} />
              </div>
              <div>
                <label className="text-sm font-medium">Subtitle</label>
                <Textarea value={content.subtitle} onChange={(e) => setEditContent({ ...editContent, subtitle: e.target.value })} rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Primary CTA</label>
                  <Input value={content.ctaPrimary} onChange={(e) => setEditContent({ ...editContent, ctaPrimary: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-medium">Secondary CTA</label>
                  <Input value={content.ctaSecondary} onChange={(e) => setEditContent({ ...editContent, ctaSecondary: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={saveEdit}><Save className="h-4 w-4 mr-2" />Save</Button>
                <Button variant="outline" onClick={cancelEdit}><X className="h-4 w-4 mr-2" />Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div><strong>Title:</strong> {content.title}</div>
              <div><strong>Subtitle:</strong> {content.subtitle}</div>
              <div><strong>Primary CTA:</strong> {content.ctaPrimary}</div>
              <div><strong>Secondary CTA:</strong> {content.ctaSecondary}</div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderPillarsEditor = (section: LandingPageSection) => {
    const isEditing = editingSection === 'pillars';
    const content = isEditing ? editContent : section.content;

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Three Pillars Section</CardTitle>
            <div className="flex items-center gap-2">
              <Switch checked={section.isActive} onCheckedChange={() => toggleMutation.mutate({ section: section.section })} />
              {section.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              {!isEditing && <Button size="sm" onClick={() => startEdit(section)}><Edit className="h-4 w-4" /></Button>}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-4">
              {[0, 1, 2].map((i) => (
                <Card key={i}>
                  <CardContent className="pt-4">
                    <div className="space-y-2">
                      <Input 
                        placeholder={`Pillar ${i + 1} Title`}
                        value={content.pillars?.[i]?.title || ''} 
                        onChange={(e) => {
                          const newPillars = [...(content.pillars || [{}, {}, {}])];
                          newPillars[i] = { ...newPillars[i], title: e.target.value };
                          setEditContent({ ...editContent, pillars: newPillars });
                        }} 
                      />
                      <Textarea 
                        placeholder="Description"
                        value={content.pillars?.[i]?.description || ''} 
                        onChange={(e) => {
                          const newPillars = [...(content.pillars || [{}, {}, {}])];
                          newPillars[i] = { ...newPillars[i], description: e.target.value };
                          setEditContent({ ...editContent, pillars: newPillars });
                        }} 
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
              <div className="flex gap-2">
                <Button onClick={saveEdit}><Save className="h-4 w-4 mr-2" />Save</Button>
                <Button variant="outline" onClick={cancelEdit}><X className="h-4 w-4 mr-2" />Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {content.pillars?.map((pillar: any, i: number) => (
                <div key={i} className="border-l-4 border-primary pl-4">
                  <div className="font-bold">{pillar.title}</div>
                  <div className="text-sm text-muted-foreground">{pillar.description}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderFeaturesEditor = (section: LandingPageSection) => {
    const isEditing = editingSection === 'features';
    const content = isEditing ? editContent : section.content;

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Features Section</CardTitle>
            <div className="flex items-center gap-2">
              <Switch checked={section.isActive} onCheckedChange={() => toggleMutation.mutate({ section: section.section })} />
              {section.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              {!isEditing && <Button size="sm" onClick={() => startEdit(section)}><Edit className="h-4 w-4" /></Button>}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-4">
              <Input value={content.title} onChange={(e) => setEditContent({ ...editContent, title: e.target.value })} placeholder="Section Title" />
              <Textarea value={content.subtitle} onChange={(e) => setEditContent({ ...editContent, subtitle: e.target.value })} placeholder="Section Subtitle" />
              {content.features?.map((feature: any, i: number) => (
                <Card key={i}>
                  <CardContent className="pt-4 space-y-2">
                    <Input 
                      placeholder="Feature Title"
                      value={feature.title} 
                      onChange={(e) => {
                        const newFeatures = [...content.features];
                        newFeatures[i] = { ...newFeatures[i], title: e.target.value };
                        setEditContent({ ...editContent, features: newFeatures });
                      }} 
                    />
                    <Textarea 
                      placeholder="Description"
                      value={feature.description} 
                      onChange={(e) => {
                        const newFeatures = [...content.features];
                        newFeatures[i] = { ...newFeatures[i], description: e.target.value };
                        setEditContent({ ...editContent, features: newFeatures });
                      }} 
                    />
                    <Input 
                      placeholder="Image URL"
                      value={feature.image} 
                      onChange={(e) => {
                        const newFeatures = [...content.features];
                        newFeatures[i] = { ...newFeatures[i], image: e.target.value };
                        setEditContent({ ...editContent, features: newFeatures });
                      }} 
                    />
                  </CardContent>
                </Card>
              ))}
              <div className="flex gap-2">
                <Button onClick={saveEdit}><Save className="h-4 w-4 mr-2" />Save</Button>
                <Button variant="outline" onClick={cancelEdit}><X className="h-4 w-4 mr-2" />Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div><strong>{content.title}</strong></div>
              <div className="text-sm text-muted-foreground">{content.subtitle}</div>
              {content.features?.map((feature: any, i: number) => (
                <div key={i} className="border-l-4 border-primary pl-4">
                  <div className="font-bold">{feature.title}</div>
                  <div className="text-sm">{feature.description}</div>
                  {feature.benefits?.map((benefit: string, j: number) => (
                    <div key={j} className="text-xs text-muted-foreground">• {benefit}</div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderCTAEditor = (section: LandingPageSection) => {
    const isEditing = editingSection === 'cta';
    const content = isEditing ? editContent : section.content;

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Call to Action Section</CardTitle>
            <div className="flex items-center gap-2">
              <Switch checked={section.isActive} onCheckedChange={() => toggleMutation.mutate({ section: section.section })} />
              {section.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              {!isEditing && <Button size="sm" onClick={() => startEdit(section)}><Edit className="h-4 w-4" /></Button>}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-4">
              <Input value={content.title} onChange={(e) => setEditContent({ ...editContent, title: e.target.value })} placeholder="Title" />
              <Textarea value={content.subtitle} onChange={(e) => setEditContent({ ...editContent, subtitle: e.target.value })} placeholder="Subtitle" />
              <Input value={content.ctaText} onChange={(e) => setEditContent({ ...editContent, ctaText: e.target.value })} placeholder="CTA Button Text" />
              <div className="flex gap-2">
                <Button onClick={saveEdit}><Save className="h-4 w-4 mr-2" />Save</Button>
                <Button variant="outline" onClick={cancelEdit}><X className="h-4 w-4 mr-2" />Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div><strong>{content.title}</strong></div>
              <div className="text-sm">{content.subtitle}</div>
              <div className="text-sm text-muted-foreground">Button: {content.ctaText}</div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderBannerEditor = (section: LandingPageSection) => {
    const isEditing = editingSection === 'banner';
    const content = isEditing ? editContent : section.content;

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Announcement Banner</CardTitle>
            <div className="flex items-center gap-2">
              <Switch checked={section.isActive} onCheckedChange={() => toggleMutation.mutate({ section: section.section })} />
              {section.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              {!isEditing && <Button size="sm" onClick={() => startEdit(section)}><Edit className="h-4 w-4" /></Button>}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-4">
              <Input value={content.text} onChange={(e) => setEditContent({ ...editContent, text: e.target.value })} placeholder="Banner Text" />
              <div className="flex gap-2">
                <Button onClick={saveEdit}><Save className="h-4 w-4 mr-2" />Save</Button>
                <Button variant="outline" onClick={cancelEdit}><X className="h-4 w-4 mr-2" />Cancel</Button>
              </div>
            </div>
          ) : (
            <div>{content.text}</div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderGenericEditor = (section: LandingPageSection) => {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="capitalize">{section.section.replace('-', ' ')}</CardTitle>
            <div className="flex items-center gap-2">
              <Switch checked={section.isActive} onCheckedChange={() => toggleMutation.mutate({ section: section.section })} />
              {section.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-60">
            {JSON.stringify(section.content, null, 2)}
          </pre>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b bg-background p-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold">Landing Page Editor</h1>
          <p className="text-sm text-muted-foreground">Edit content for each section of your landing page</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {sections?.map((section) => (
          <div key={section._id}>
            {section.section === 'hero' && renderHeroEditor(section)}
            {section.section === 'pillars' && renderPillarsEditor(section)}
            {section.section === 'features' && renderFeaturesEditor(section)}
            {section.section === 'cta' && renderCTAEditor(section)}
            {section.section === 'banner' && renderBannerEditor(section)}
            {!['hero', 'pillars', 'features', 'cta', 'banner'].includes(section.section) && renderGenericEditor(section)}
          </div>
        ))}
      </div>
    </div>
  );
}
